import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X, Plus, Check, Trash2, Pencil, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import unitTypeService from '../../services/unitTypeService';

const SLATE = '#475569';

/**
 * Admin modal to manage the product "Unit Type" dropdown options (BMF-87).
 * Add / rename / deactivate / delete unit types. Built-ins power variant pricing
 * so they can only be hidden (not renamed/deleted); custom types are label-only
 * and can't be deleted while products still use them (the API enforces both).
 */
export default function ManageUnitTypes({ onClose, onChanged }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await unitTypeService.getAll();
      setUnits(data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load unit types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const notifyChanged = () => { if (onChanged) onChanged(); };
  const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

  const handleAdd = async (e) => {
    e.preventDefault();
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    try {
      const created = await unitTypeService.create(label);
      // A new custom type gets the highest sortOrder, so it belongs at the end of
      // the list. Update local state from the response instead of a full refetch.
      setUnits((prev) => [...prev, { ...created, productCount: 0 }]);
      setNewLabel('');
      toast.success(`Added "${label}"`);
      notifyChanged();
    } catch (err) {
      toast.error(errMsg(err, 'Could not add unit type'));
      load(); // resync from server on failure
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (u) => { setEditingId(u.id); setEditLabel(u.label); };
  const cancelEdit = () => { setEditingId(null); setEditLabel(''); };

  const saveEdit = async (u) => {
    const label = editLabel.trim();
    if (!label || label === u.label) { cancelEdit(); return; }
    setBusyId(u.id);
    try {
      const updated = await unitTypeService.update(u.id, { label });
      // Rename doesn't change usage, so keep the existing productCount (the API
      // returns the bare entity without it).
      setUnits((prev) => prev.map((x) => (x.id === u.id ? { ...updated, productCount: x.productCount } : x)));
      toast.success('Unit type renamed');
      cancelEdit();
      notifyChanged();
    } catch (err) {
      toast.error(errMsg(err, 'Could not rename'));
      load(); // resync from server on failure
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (u) => {
    setBusyId(u.id);
    try {
      const updated = await unitTypeService.update(u.id, { isActive: !u.isActive });
      setUnits((prev) => prev.map((x) => (x.id === u.id ? { ...updated, productCount: x.productCount } : x)));
      notifyChanged();
    } catch (err) {
      toast.error(errMsg(err, 'Could not update'));
      load(); // resync from server on failure
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete unit type "${u.label}"? This can't be undone.`)) return;
    setBusyId(u.id);
    try {
      await unitTypeService.remove(u.id);
      setUnits((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`Deleted "${u.label}"`);
      notifyChanged();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete'));
      load(); // resync from server on failure
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Unit Types</h2>
            <p className="text-xs text-gray-500">Options shown in the product "Unit Type" dropdown</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add form */}
        <form onSubmit={handleAdd} className="flex gap-2 px-5 py-3 border-b border-gray-100">
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            maxLength={50}
            placeholder="New unit type (e.g. Bunch, Dozen, 2 Kg)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
          <button
            type="submit"
            disabled={adding || !newLabel.trim()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-medium transition-opacity disabled:opacity-50"
            style={{ backgroundColor: SLATE }}
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </form>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : units.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-10">No unit types yet.</p>
          ) : (
            units.map((u) => (
              <div
                key={u.id}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${u.isActive ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}
              >
                {editingId === u.id ? (
                  <>
                    <input
                      autoFocus
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      maxLength={50}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(u);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    />
                    <button onClick={() => saveEdit(u)} disabled={busyId === u.id} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded" aria-label="Save">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={cancelEdit} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded" aria-label="Cancel">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${u.isActive ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{u.label}</span>
                        {u.isBuiltin && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600">
                            <Lock className="w-3 h-3" /> Built-in
                          </span>
                        )}
                        {!u.isActive && <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-200 text-gray-500">Inactive</span>}
                      </div>
                      <span className="text-[11px] text-gray-400">
                        {u.productCount} product{u.productCount === 1 ? '' : 's'}
                      </span>
                    </div>

                    <button
                      onClick={() => toggleActive(u)}
                      disabled={busyId === u.id}
                      title={u.isActive ? 'Deactivate (hide from dropdown)' : 'Activate'}
                      className={`px-2 py-1 text-xs rounded font-medium ${u.isActive ? 'text-amber-700 hover:bg-amber-50' : 'text-green-700 hover:bg-green-50'}`}
                    >
                      {u.isActive ? 'Deactivate' : 'Activate'}
                    </button>

                    {!u.isBuiltin && (
                      <button onClick={() => startEdit(u)} disabled={busyId === u.id} title="Rename" className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" aria-label="Rename">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}

                    {!u.isBuiltin && (
                      <button
                        onClick={() => handleDelete(u)}
                        disabled={busyId === u.id || u.productCount > 0}
                        title={u.productCount > 0 ? 'In use — deactivate instead of deleting' : 'Delete'}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer note */}
        <div className="px-5 py-3 border-t border-gray-100">
          <p className="text-[11px] text-gray-400">
            Built-in types power variant pricing, so they can be hidden but not renamed or deleted.
            Custom types are label-only — products using them can't have variants.
          </p>
        </div>
      </div>
    </div>
  );
}

ManageUnitTypes.propTypes = {
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};
